import React, { useEffect, useRef, useState } from "react";
import { Col, Button, Form as BootstrapForm } from "react-bootstrap";
import axios from "axios";
import Config from "../Config/Config";
import randomString from "randomstring";
import { FiRefreshCcw } from "react-icons/fi";
import toast from "react-hot-toast";

const InquiryForm = ({ Tabs_records }) => {
  const [captchaText, setCaptchaText] = useState("");
  const canvasRef = useRef(null);
  const [loading , setLoading] = useState(false)
  const [formData, setFormData] = useState({
    fullName: "",
    mobileNumber: "",
    tripType: "",
    pickupDate: "",
    pickupTime: "",
    captureText: "",
    from_city_name: "",
    state_name: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Populate tab record data if available
  useEffect(() => {
    if (Tabs_records) {
      setFormData((prev) => ({
        ...prev,
        tripType: Tabs_records?.trip_type || "",
        from_city_name: Tabs_records?.from_city_name || "",
        state_name: Tabs_records?.state_name || "",
      }));
    }
  }, [Tabs_records]);

  // Generate time options (every 30 mins)
  const timeOptions = Array.from({ length: 48 }, (_, i) => {
    const hour = Math.floor(i / 2);
    const minute = i % 2 === 0 ? "00" : "30";
    const ampm = hour < 12 ? "AM" : "PM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute} ${ampm}`;
  });

  // ✅ Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ✅ Manual Validation
  const validateForm = () => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = "Full Name is required";
    } else if (formData.fullName.trim().length < 2) {
      errors.fullName = "Full Name must be at least 2 characters";
    }

    if (!formData.mobileNumber) {
      errors.mobileNumber = "Mobile Number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      errors.mobileNumber = "Mobile Number must be exactly 10 digits";
    }

    // if (!formData.tripType) {
    //   errors.tripType = "Please select a trip type";
    // }

    if (!formData.pickupDate) {
      errors.pickupDate = "Please select a pickup date";
    }

    if (!formData.pickupTime) {
      errors.pickupTime = "Please select a pickup time";
    }

    if (!formData.captureText) {
      errors.captureText = "Please enter the captcha";
    } else if (formData.captureText !== captchaText) {
      errors.captureText = "Captcha does not match";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Captcha Generation
  const generateCaptchaText = () => {
    return randomString.generate({ length: 5, charset: "alphanumeric" });
  };

  const generateCaptchaImage = () => {
    const text = generateCaptchaText();
    setCaptchaText(text);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let frame = 0;
    const drawFrame = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, "rgba(20, 20, 20, 0.8)");
      gradient.addColorStop(1, "rgba(60, 60, 60, 0.8)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Random dots
      for (let i = 0; i < 100; i++) {
        ctx.fillStyle = `rgba(255, 255, 255, ${Math.random() * 0.1})`;
        ctx.beginPath();
        ctx.arc(Math.random() * canvas.width, Math.random() * canvas.height, Math.random() * 2, 0, Math.PI * 2);
        ctx.fill();
      }

      // Wave lines
      for (let i = 0; i < 3; i++) {
        ctx.strokeStyle = `rgba(255, 255, 255, 0.2)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        let y = Math.sin(frame * 0.02 + i) * 5 + canvas.height / 2;
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y + Math.sin(frame * 0.05) * 8);
        ctx.stroke();
      }

      // Text
      ctx.font = "bold 26px Arial";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(Math.sin(frame * 0.03) * 0.1);
      ctx.fillText(text, 0, 8);
      ctx.restore();

      frame++;
      requestAnimationFrame(drawFrame);
    };
    drawFrame();
  };

  useEffect(() => {
    generateCaptchaImage();
  }, []);

  // ✅ Handle Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submissionData = {
      name: formData.fullName.trim(),
      mobile_no: formData.mobileNumber.trim(),
      email_id: "",
      pickup_city: `${formData.from_city_name || "ahmadabad"} , ${
        formData.state_name || "Gujarat"
      }`,
      drop_city: "",
      no_of_passengers: "1",
      trip_type: formData.tripType,
      pickup_date: `${formData.pickupDate} ${formData.pickupTime}`,
      pickup_time: formData.pickupTime,
      source: window.location.href
    };

    setLoading(true)

    try {
      const response = await axios.post(`${Config.API_URL}/api/customer/save/inquiry`, submissionData, {
        headers: { "Content-Type": "application/json" },
      });

      if (response.data.status) {
        toast.success(response.data.message);
        // Reset form
        setFormData({
          fullName: "",
          mobileNumber: "",
          tripType: "",
          pickupDate: "",
          pickupTime: "",
          captureText: "",
          from_city_name: "",
          state_name: "",
        });
        generateCaptchaImage();
      }
    } catch (err) {
      console.error(err.message);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false)
    }
  };

  return (
    <BootstrapForm onSubmit={handleSubmit}>
      {/* Full Name */}
      <Col xs={12} md={12}>
        <BootstrapForm.Group className="mb-3" controlId="fullName">
          <input
            type="text"
            name="fullName"
            placeholder="Enter your name"
            value={formData.fullName}
            onChange={handleInputChange}
            className={`form-control ${formErrors.fullName ? "is-invalid" : ""}`}
          />
          {formErrors.fullName && <div className="text-danger">{formErrors.fullName}</div>}
        </BootstrapForm.Group>
      </Col>

      {/* Mobile Number */}
      <Col xs={12} md={12}>
        <BootstrapForm.Group className="mb-3" controlId="mobileNumber">
          <input
            type="text"
            name="mobileNumber"
            placeholder="Enter your mobile number"
            value={formData.mobileNumber}
            onChange={handleInputChange}
            maxLength={10}
            onKeyPress={(e) => {
              if (!/[0-9]/.test(e.key)) e.preventDefault();
            }}
            className={`form-control ${formErrors.mobileNumber ? "is-invalid" : ""}`}
          />
          {formErrors.mobileNumber && <div className="text-danger">{formErrors.mobileNumber}</div>}
        </BootstrapForm.Group>
      </Col>

      {/* Trip Type */}
      <Col md={12}>
        <div style={{ marginBottom: "15px" }}>
          <select
            name="tripType"
            value={formData.tripType}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              border: formErrors.tripType ? "1px solid red" : "1px solid #ddd",
              borderRadius: "5px",
              // fontSize: "12px",
              height: "40px",
            }}
          >
            <option value="">Select Trip Type</option>
            <option value="oneway">One Way</option>
            <option value="outstation">Outstation</option>
            <option value="local">Local</option>
            <option value="airport">Airport</option>
          </select>
          {formErrors.tripType && (
            <span style={{ color: "red", fontSize: "12px", marginTop: "5px", display: "block" }}>
              {formErrors.tripType}
            </span>
          )}
        </div>
      </Col>

      {/* Pickup Date & Time */}
      <Col md={12} className="d-flex gap-1">
        <div style={{ width: "55%" }}>
          <input
            type="date"
            name="pickupDate"
            value={formData.pickupDate}
            onChange={handleInputChange}
            min={new Date().toISOString().split("T")[0]}
            placeholder="Date"
            style={{
              width: "100%",
              padding: "10px",
              border: formErrors.pickupDate ? "1px solid red" : "1px solid #ddd",
              borderRadius: "5px",
              height: "40px",
            }}
          />
          {formErrors.pickupDate && (
            <div style={{ color: "red", fontSize: "12px" }}>{formErrors.pickupDate}</div>
          )}
        </div>
        <div style={{ width: "45%" }}>
          <select
            name="pickupTime"
            value={formData.pickupTime}
            onChange={handleInputChange}
            placeholder="Time"
            style={{
              width: "100%",
              padding: "10px",
              border: formErrors.pickupTime ? "1px solid red" : "1px solid #ddd",
              borderRadius: "5px",
              height: "40px",
              // fontSize: "12px",
            }}
          >
            <option value="">Time</option>
            {timeOptions.map((time, index) => (
              <option key={index} value={time}>
                {time}
              </option>
            ))}
          </select>
          {formErrors.pickupTime && (
            <div style={{ color: "red", fontSize: "12px" }}>{formErrors.pickupTime}</div>
          )}
        </div>
      </Col>

      {/* Captcha */}
      <Col xs={12} md={12} className="d-flex justify-content-between align-items-center my-2">
        <canvas ref={canvasRef} width="150" height="40" style={{ border: "1px solid black" }}></canvas>
        <button type="button" onClick={generateCaptchaImage} className="btn btn-secondary btn-sm">
          <FiRefreshCcw />
        </button>
      </Col>

      {/* Captcha Input */}
      <Col xs={12} md={12}>
        <BootstrapForm.Group className="mb-3">
          <input
            type="text"
            name="captureText"
            placeholder="Enter the Captcha"
            value={formData.captureText}
            onChange={handleInputChange}
            className={`form-control ${formErrors.captureText ? "is-invalid" : ""}`}
          />
          {formErrors.captureText && (
            <div className="text-danger">{formErrors.captureText}</div>
          )}
        </BootstrapForm.Group>
      </Col>

      {/* Submit Button */}
      <Col xs={12} md={12}>
        <Button className="sitebtn" type="submit" disabled={loading}>
          { loading ? "Submitting..." : "Submit"}
        </Button>
      </Col>  
    </BootstrapForm>
  );
};

export default InquiryForm;
